import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retryWhen, mergeMap } from 'rxjs/operators';
import { ErrorLoggingService } from './error-logging.service';
import { Router } from '@angular/router';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private errorLoggingService: ErrorLoggingService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      // נסה שוב רק על שגיאות זמניות (network errors, timeouts)
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error: HttpErrorResponse, index) => {
            // Retry only on network errors or specific transient errors
            // Never for Ezri: a retried message is a second paid model call (and a
            // second history entry), and 429/503 there are answers, not glitches.
            // Nor for receipt uploads: a retried scan is a second paid scan.
            const noRetry = request.url.includes('/api/assistant/') || request.url.includes('/api/receipts');
            const shouldRetry = !noRetry && (
              error.status === 0 ||   // Network error
              error.status === 408 || // Request Timeout
              error.status === 429 || // Too Many Requests
              error.status === 503 || // Service Unavailable
              error.status === 504    // Gateway Timeout
            ) && index < 1; // Retry only once

            if (shouldRetry) {
              return timer(1000); // Wait 1 second before retry
            }
            return throwError(() => error);
          })
        )
      ),
      catchError((error: HttpErrorResponse) => {
        let errorMessage = '';

        if (error.error instanceof ErrorEvent) {
          // Client-side error
          errorMessage = `Client Error: ${error.error.message}`;
        } else {
          // Server-side error
          errorMessage = `Server Error: ${error.status} - ${error.message}`;
        }

        // Don't handle 401 errors here - let AuthInterceptor handle them for token refresh
        if (error.status === 401) {
          console.log('[HttpErrorInterceptor] 401 detected, passing through to AuthInterceptor');
          return throwError(() => error);
        }

        // לוג את השגיאה רק אם היא לא מהסוג שאנחנו רוצים להתעלם ממנו
        if (this.shouldLogError(error, request)) {
          const customError = new Error(errorMessage);
          customError.stack = `HTTP Error\nURL: ${request.url}\nMethod: ${request.method}\nStatus: ${error.status}`;

          this.errorLoggingService.logError(customError, {
            route: this.router.url,
            action: `HTTP ${request.method}`,
            additionalData: JSON.stringify({
              url: request.url,
              status: error.status,
              statusText: error.statusText
            })
          });
        }

        // טיפול בשגיאות נפוצות
        this.handleSpecificErrors(error);

        return throwError(() => error);
      })
    );
  }

  /**
   * מחליט אם לשמור את השגיאה בלוג
   */
  private shouldLogError(error: HttpErrorResponse, request: HttpRequest<any>): boolean {
    // אל תשמור לוג לבקשות logging עצמן (למנוע אינסוף לולאה)
    if (request.url.includes('/api/logs/client-error')) {
      return false;
    }

    // אל תשמור לוג כשאין חיבור: השגיאה צפויה, והלוג עצמו לא יגיע לשרת
    if (error.status === 0 && typeof navigator !== 'undefined' && !navigator.onLine) {
      return false;
    }

    // אל תשמור לוג ל-401 (לא מורשה - זה צפוי)
    if (error.status === 401) {
      return false;
    }

    // אל תשמור לוג ל-404 על תמונות/קבצים (לא קריטי)
    if (error.status === 404 && this.isAssetRequest(request)) {
      return false;
    }

    // אל תשמור לוג ל-404 על endpoints שמחזירים 404 כאשר משאב לא קיים (צפוי)
    if (error.status === 404 && this.isExpected404(request)) {
      return false;
    }

    // אל תשמור לוג ל-status 0 על endpoints שבודקים מצב ברקע (הקומפוננטה מטפלת בשגיאה בשקט)
    if (error.status === 0 && this.isSilentBackgroundEndpoint(request)) {
      return false;
    }

    return true;
  }

  /**
   * בודק אם זו בקשת רקע שהקומפוננטה מטפלת בשגיאה שלה בשקט
   */
  private isSilentBackgroundEndpoint(request: HttpRequest<any>): boolean {
    const silentEndpoints = [
      '/api/TimeEntry/active', // FloatingTimer ו-TimerComponent בודקים ברקע
      '/auth/status',          // בדיקת auth בלי הפרעה
    ];
    return silentEndpoints.some(endpoint => request.url.toLowerCase().includes(endpoint.toLowerCase()));
  }

  /**
   * בודק אם ה-404 הוא צפוי (משאב שעדיין לא נוצר)
   */
  private isExpected404(request: HttpRequest<any>): boolean {
    const expected404Endpoints = [
      '/api/businessprofile',    // פרופיל עסקי - לא קיים למשתמשים חדשים
      '/api/business/',          // פרטי עסק
    ];
    return expected404Endpoints.some(endpoint => request.url.includes(endpoint));
  }

  /**
   * בודק אם זו בקשה לקובץ סטטי
   */
  private isAssetRequest(request: HttpRequest<any>): boolean {
    const assetExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.css', '.js', '.ico'];
    return assetExtensions.some(ext => request.url.toLowerCase().endsWith(ext));
  }

  /**
   * טיפול בשגיאות ספציפיות
   */
  private handleSpecificErrors(error: HttpErrorResponse): void {
    switch (error.status) {
      // 401 is handled by AuthInterceptor for token refresh - don't handle here

      case 403:
        // אין הרשאה
        console.warn('Forbidden - you do not have permission');
        break;

      case 404:
        // לא נמצא
        console.warn('Resource not found:', error.url);
        break;

      case 500:
      case 502:
      case 503:
        // שגיאת שרת
        console.error('Server error:', error.status);
        break;

      default:
        console.error('HTTP error:', error);
    }
  }
}
